import { useState, useEffect } from 'react';
import { useAuth } from './hooks/useAuth.js';
import { getMe } from './api/patients.js';
import { StepperMobile } from './components/Stepper.jsx';
import LandingScreen from './components/LandingScreen.jsx';
import ExamSearchScreen from './components/ExamSearchScreen.jsx';
import LoginScreen from './components/LoginScreen.jsx';
import SignUpScreen from './components/SignUpScreen.jsx';
import Step1Upload from './components/Step1Upload.jsx';
import Step2Confirm from './components/Step2Confirm.jsx';
import Step3Location from './components/Step3Location.jsx';
import Step4Labs from './components/Step4Labs.jsx';
import Step5Payment from './components/Step5Payment.jsx';
import Step6Confirm from './components/Step6Confirm.jsx';
import AccountMenu from './components/AccountMenu.jsx';
import ProfileEditScreen from './components/ProfileEditScreen.jsx';
import OrdersScreen from './components/OrdersScreen.jsx';
import ResultsScreen from './components/ResultsScreen.jsx';

const STEPS = ['Definir Serviços', 'Exames', 'Locais', 'Laboratório', 'Pagamento', 'Confirmação'];

export default function App() {
  const { isAuthed, loading: authLoading, login, signup, logout } = useAuth();
  // 'landing' | 'search' | 'login' | 'signup' | 'flow'
  const [screen, setScreen] = useState('landing');
  const [step, setStep] = useState(0);
  const [flow, setFlow] = useState({});
  const [selectedExam, setSelectedExam] = useState(null);
  const [startUpload, setStartUpload] = useState(false);
  const [flowAuthView, setFlowAuthView] = useState('login'); // 'login' | 'signup' — gate de auth do Step5
  const [profile, setProfile] = useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountView, setAccountView] = useState(null); // null | 'profile' | 'orders' | 'results'
  const [resultsOrderFilter, setResultsOrderFilter] = useState(null);

  const goLanding = () => { setScreen('landing'); setStep(0); setFlow({}); };

  // Sessão expirada (401): a sessão já foi apagada (useAuth + interceptor);
  // volta para a home, onde o botão "Entrar" fica disponível de novo.
  useEffect(() => {
    const handle = () => {
      setScreen('landing'); setStep(0); setFlow({});
      setAccountView(null); setAccountMenuOpen(false); setResultsOrderFilter(null);
    };
    window.addEventListener('iex:unauthorized', handle);
    return () => window.removeEventListener('iex:unauthorized', handle);
  }, []);

  // Carrega o nome do paciente quando autenticado (para o avatar com iniciais).
  // Espera `authLoading` baixar antes de buscar: o cadastro é 2 passos (iam
  // primeiro, depois o Patient) e os tokens do passo 1 já deixam `isAuthed`
  // true antes do passo 2 terminar — buscar o perfil nesse meio-tempo 401a
  // ("Cadastro de paciente incompleto"), o que limpa a sessão via interceptor
  // mesmo que o cadastro todo tenha sido concluído com sucesso logo em seguida.
  useEffect(() => {
    if (!isAuthed) { setProfile(null); return; }
    if (authLoading) return;
    let alive = true;
    getMe()
      .then(p => { if (alive) setProfile(p); })
      .catch(() => {}); // 401 já limpa a sessão via interceptor
    return () => { alive = false; };
  }, [isAuthed, authLoading]);

  const startFlow = (exam = null, upload = false) => {
    setSelectedExam(exam);
    setStartUpload(upload);
    setStep(0);
    setFlow({});
    setFlowAuthView('login');
    setScreen('flow');
  };

  // Login só é exigido no Step5 (pagamento) — o wizard inteiro (upload do
  // pedido médico, exames, local, laboratório) fica livre sem estar logado.
  const handleBuy = (exam) => startFlow(exam);

  const handleUploadPrescription = () => startFlow(null, true);

  const handleLogin = async (email, password) => {
    await login(email, password);
    // Dentro do wizard (gate do Step5), `screen` já é 'flow' e deve
    // continuar — só o login "solo" (nav da home) navega para 'landing'.
    if (screen !== 'flow') setScreen('landing');
  };

  const handleSignup = async (form) => {
    await signup(form);
    if (screen !== 'flow') setScreen('landing');
  };

  const onLogout = () => {
    logout();
    setScreen('landing');
    setStep(0);
    setFlow({});
    setSelectedExam(null);
    setStartUpload(false);
    setAccountView(null);
    setResultsOrderFilter(null);
  };

  const stepProps = {
    state: flow,
    setState: setFlow,
    onNext: () => setStep(s => Math.min(s + 1, 5)),
    onBack: step === 0 ? goLanding : () => setStep(s => s - 1),
  };

  const renderStep = () => {
    if (step === 0) return <Step1Upload {...stepProps} preselectedExam={selectedExam} autoPrescription={startUpload}/>;
    if (step === 1) return <Step2Confirm {...stepProps}/>;
    if (step === 2) return <Step3Location {...stepProps}/>;
    if (step === 3) return <Step4Labs {...stepProps}/>;
    if (step === 4) return <Step5Payment {...stepProps} profile={profile} onEditAddress={() => setAccountView('profile')}/>;
    return <Step6Confirm state={flow} onRestart={goLanding}/>;
  };

  const renderScreen = () => {
    if (isAuthed && accountView === 'profile' && profile) {
      return (
        <ProfileEditScreen
          user={profile}
          onSave={setProfile}
          onBack={() => setAccountView(null)}
        />
      );
    }

    if (isAuthed && accountView === 'orders') {
      return (
        <OrdersScreen
          onBack={() => setAccountView(null)}
          onOpenResult={(order) => { setResultsOrderFilter(order.protocol_number); setAccountView('results'); }}
        />
      );
    }

    if (isAuthed && accountView === 'results') {
      return (
        <ResultsScreen
          onBack={() => setAccountView(null)}
          initialOrderId={resultsOrderFilter}
        />
      );
    }

    if (screen === 'flow') {
      // Gate de login: só o passo de pagamento (Step5, index 4) exige auth.
      if (step === 4 && !isAuthed) {
        return flowAuthView === 'signup'
          ? <SignUpScreen onBack={() => setFlowAuthView('login')} onCreated={handleSignup}/>
          : <LoginScreen onLogin={handleLogin} onBack={() => setStep(3)} onSignup={() => setFlowAuthView('signup')}/>;
      }
      return (
        <div className="screen" style={{ position: 'relative' }}>
          <StepperMobile steps={STEPS} current={step} user={profile} onProfileClick={() => setAccountMenuOpen(true)}/>
          {renderStep()}
        </div>
      );
    }

    if (screen === 'search') {
      return <ExamSearchScreen onBack={() => setScreen('landing')} onBuy={handleBuy} onUploadPrescription={handleUploadPrescription}/>;
    }

    if (screen === 'signup') {
      return <SignUpScreen onBack={() => setScreen('login')} onCreated={handleSignup}/>;
    }

    if (screen === 'login') {
      return (
        <LoginScreen
          onLogin={handleLogin}
          onBack={() => setScreen('landing')}
          onSignup={() => setScreen('signup')}
        />
      );
    }

    return (
      <LandingScreen
        onBuy={handleBuy}
        onSearch={() => setScreen('search')}
        onLogin={() => { setSelectedExam(null); setScreen('login'); }}
        user={isAuthed ? profile : null}
        onOpenAccountMenu={() => setAccountMenuOpen(true)}
      />
    );
  };

  return (
    <>
      {renderScreen()}
      {isAuthed && (
        <AccountMenu
          open={accountMenuOpen}
          onClose={() => setAccountMenuOpen(false)}
          profile={profile}
          onNavigate={(view) => { setAccountMenuOpen(false); setAccountView(view); }}
          onLogout={() => { setAccountMenuOpen(false); onLogout(); }}
        />
      )}
    </>
  );
}
